/**
 * BeamLinkSystem — the two-endpoint follower (beam-builders spec §4.2).
 *
 * H2000 had TWO disjoint beam systems: the registry `beam` primitive
 * (composable, grammar-visible, but horizontal-only — a beam could only
 * point left/right at a fixed authored length) and the hitscan handler
 * (two-endpoint, but outside the registry: no primitives, no palette, no
 * expectations hookup). This system unifies them at the motion layer: a
 * primitive carrying `link` is stretched + rotated from the effect's caster
 * anchor to a second entity EVERY frame, so registry-composed beams track
 * both moving endpoints.
 *
 * Sibling of FollowerSystem/OrbiterSystem — same add/remove/update/clear
 * shape, pumped from VFXEffectPlayer.update with the same resolveTarget.
 * Per frame, for a stretch link:
 *   1. obj.x/y   = caster anchor (the beam primitive draws from (0,0)
 *      along +x — ShapeSpawner's local-origin convention makes rotation
 *      and scaleX safe: the caster end stays put).
 *   2. obj.rotation = atan2(dy, dx) toward the endpoint.
 *   3. obj.scaleX  = dist / authoredLength (clamped by maxLengthPx).
 *      scaleY is UNTOUCHED so width pulses compose cleanly.
 * Mote links (link.mote) FLOW instead: position lerps endpoint→endpoint on
 * a periodMs cycle (drain packets — the "life flowing back" read).
 *
 * Endpoint rules:
 *  - targetId resolves → real link. ~4Hz {from,to} samples are recorded
 *    into BeamLinkStore for the verifier's motion:'beam' checks (spec §7).
 *  - no targetId (targetless channel cast) → fallback: a virtual endpoint
 *    at casterAnchor + fallbackDx (facing-aligned fixed reach — the
 *    HitscanBehavior floor). Samples record `linked: false`.
 *  - target WAS resolvable and died → freeze the last transform and let the
 *    duration kill fade it (FollowerSystem's missing-target tolerance —
 *    never hard-pop).
 */

import type { IShape } from '../../rendering/IRenderer';
import { entityVfxAnchor } from './entityVfxAnchor';
import { BeamLinkStore } from '../../diagnostics/BeamLinkStore';

/** ~4Hz sampling into BeamLinkStore (spec §7). */
const SAMPLE_INTERVAL_MS = 250;

export interface BeamLinkOpts {
  /** Anchor entity for the caster end (resolved via resolveTarget). */
  casterId: string;
  /** Second endpoint entity; absent = targetless fallback. */
  targetId?: string;
  /** For BeamLinkStore rows (verifier correlation). */
  abilityId?: string | null;
  /** The primitive's authored beamLength — scaleX = dist / authoredLength. */
  authoredLength: number;
  /** Clamp on the stretched length (jitter scales with stretch — spec §9). */
  maxLengthPx?: number;
  /** Fallback virtual endpoint offset (signed by facing) when no target. */
  fallbackDx: number;
  /** Flow along the link instead of stretching across it (drain motes). */
  mote?: { periodMs: number; phase?: number; direction?: 'toCaster' | 'toTarget' };
}

interface BeamLinkData extends BeamLinkOpts {
  cachedCaster?: any;
  cachedTarget?: any;
  /** Target resolved at least once — a later null means DEAD, not pending. */
  targetSeen: boolean;
  frozen: boolean;
  lastSampleTs: number;
}

export class BeamLinkSystem {
  private links = new Map<IShape, BeamLinkData>();

  /** Register a linked shape. */
  public add(obj: IShape, opts: BeamLinkOpts): void {
    this.links.set(obj, {
      ...opts,
      targetSeen: false,
      frozen: false,
      lastSampleTs: 0,
    });
  }

  /** Remove a shape from the link system. */
  public remove(obj: IShape): void {
    this.links.delete(obj);
  }

  /** Live link count (diagnostics). */
  public getActiveCount(): number {
    return this.links.size;
  }

  /** Update all links. Call from VFXEffectPlayer.update(). */
  public update(resolveTarget: (id: string) => any, now?: number): void {
    const ts = now ?? Date.now();
    this.links.forEach((data, obj) => {
      if (!obj.active) {
        this.links.delete(obj);
        return;
      }
      if (data.frozen) return; // dead endpoint — hold the last transform

      let caster = data.cachedCaster;
      if (!caster || caster.isDestroyed || !caster.position) {
        caster = resolveTarget(data.casterId);
        data.cachedCaster = caster;
      }
      const casterAnchor = entityVfxAnchor(caster);
      if (!casterAnchor) {
        // Caster unresolvable: dead caster after having rendered → freeze;
        // never-resolved (headless/degrade) → leave the spawn transform.
        if (data.targetSeen || data.lastSampleTs > 0) data.frozen = true;
        return;
      }

      let endpoint: { x: number; y: number } | null = null;
      let linked = false;
      if (data.targetId) {
        let target = data.cachedTarget;
        if (!target || target.isDestroyed || !target.position) {
          target = resolveTarget(data.targetId);
          data.cachedTarget = target;
        }
        const targetAnchor = entityVfxAnchor(target);
        if (targetAnchor) {
          endpoint = targetAnchor;
          linked = true;
          data.targetSeen = true;
        } else if (data.targetSeen) {
          // Target died mid-channel — freeze and let the duration kill fade.
          data.frozen = true;
          return;
        }
      }
      if (!endpoint) {
        // Targetless fallback: facing-aligned fixed reach off the caster.
        endpoint = { x: casterAnchor.x + data.fallbackDx, y: casterAnchor.y };
      }

      if (data.mote) {
        this.placeMote(obj, data, casterAnchor, endpoint, ts);
      } else {
        this.placeBeam(obj, data, casterAnchor, endpoint);
        if (ts - data.lastSampleTs >= SAMPLE_INTERVAL_MS) {
          data.lastSampleTs = ts;
          BeamLinkStore.record({
            ts,
            abilityId: data.abilityId ?? null,
            casterId: data.casterId ?? null,
            from: { x: casterAnchor.x, y: casterAnchor.y },
            to: { x: endpoint.x, y: endpoint.y },
            linked,
          });
        }
      }
    });
  }

  public clear(): void {
    this.links.clear();
  }

  /** Stretch + rotate the beam graphic caster→endpoint (spec §4.2). */
  private placeBeam(
    obj: IShape,
    data: BeamLinkData,
    casterAnchor: { x: number; y: number },
    endpoint: { x: number; y: number },
  ): void {
    const dx = endpoint.x - casterAnchor.x;
    const dy = endpoint.y - casterAnchor.y;
    let d = Math.hypot(dx, dy);
    if (data.maxLengthPx !== undefined) d = Math.min(d, data.maxLengthPx);
    obj.x = casterAnchor.x;
    obj.y = casterAnchor.y;
    (obj as any).rotation = Math.atan2(dy, dx);
    (obj as any).scaleX = data.authoredLength > 0 ? d / data.authoredLength : 1;
    // scaleY untouched — width pulses (scaleY tweens) compose cleanly.
  }

  /** Lerp a mote along the link on its periodMs cycle (spec §4.3). */
  private placeMote(
    obj: IShape,
    data: BeamLinkData,
    casterAnchor: { x: number; y: number },
    endpoint: { x: number; y: number },
    ts: number,
  ): void {
    const mote = data.mote!;
    const period = Math.max(1, mote.periodMs);
    const t = ((ts / period) + (mote.phase ?? 0)) % 1;
    const toCaster = (mote.direction ?? 'toCaster') === 'toCaster';
    const from = toCaster ? endpoint : casterAnchor;
    const to = toCaster ? casterAnchor : endpoint;
    obj.x = from.x + (to.x - from.x) * t;
    obj.y = from.y + (to.y - from.y) * t;
  }
}
